/**
 * useFteAllocations — reactively computes FTE allocations for all budget nodes.
 *
 * Replaces the one-time computation in App.tsx auto-import. Recomputes whenever
 * nodes or resourceConfig change, so InvestmentTable, ProductTable, and charts
 * always show current FTE values.
 *
 * Formula: node FTE = nodeInvestment / (avgFteCost × fteEfficiency) — `nodeFte`,
 * shared with the Finance and Resources charts (WQ-343).
 */

import { useMemo } from 'react';
import { useAdjacencyStore } from '../store/adjacency-store';
import { nodeFte } from '../utils/fte';

export function useFteAllocations(): Map<string, number> {
  const nodes = useAdjacencyStore((s) => s.nodes);
  const resourceConfig = useAdjacencyStore((s) => s.resourceConfig);

  return useMemo(() => {
    const effectiveCost = resourceConfig.avgFteCost * resourceConfig.fteEfficiency;
    if (effectiveCost === 0) return new Map<string, number>();

    const allocations = new Map<string, number>();
    for (const node of nodes) {
      const nodeInvestment = (node.iRnD ?? 0) + (node.fRnD ?? 0);
      if (nodeInvestment > 0) {
        allocations.set(node.id, nodeFte(node, resourceConfig));
      }
    }
    return allocations;
  }, [nodes, resourceConfig]);
}
